import { loadSession, startVersionWatch, state } from './state.ts';
import { navigate, resolve, route, setGuard, setNotFound, setPageMeta, startRouter } from './router.ts';
import { appMain, ensureAppShell } from './ui/shell.ts';
import { stateBlock } from './ui/feedback.ts';
import { el, mount } from './ui/dom.ts';
import { isPlaying } from './player/player.ts';
import { landingView } from './views/landing.ts';
import {
  discoverView, iptvView, libraryView, liveView, searchView, seriesDetailView,
} from './views/browse.ts';
import { mockPaymentView, plansView, settingsView } from './views/account.ts';
import { compatibilityView, termsView } from './views/legal.ts';
import { authView, forgotView, resetView } from './views/auth.ts';
import { handoffView } from './views/handoff.ts';
import { checkoutDetailsView, checkoutDoneView } from './views/checkout.ts';
import { adminUserView, adminView } from './views/admin.ts';
import { startStats } from './stats.ts';
import { onSurfaceChange, startSurfaceWatch } from './ui/surface.ts';
import { applyDocumentLang, t } from './i18n/index.ts';
import { BRAND } from './ui/brand.ts';

// Rota haritası:
//   /               herkese açık landing — otomatik /login yönlendirmesi YOK
//   /login          giriş / kayıt (?mode=signup)
//   /forgot /reset  parola sıfırlama (bağlantı iste / yeni parola belirle)
//   /legal          yasal metinler (?section=terms|privacy|cookies|kvkk|subscription|safety|copyright|contact)
//   /compatibility  cihaz uyumluluğu (herkese açık)
//   /browse         uygulama ana ekranı (Keşfet)
//   /search /live /iptv /library /series/:id /plans /settings /checkout/test /handoff
// Eski Türkçe yollar (yer imleri, paylaşılan adresler) yenilerine yönlenir.
// Sunucudaki KNOWN_ROUTES (src/server/app.ts) bu listeyle aynı olmalı.

route('/', false, () => {
  setPageMeta({ title: t('meta.home'), index: true, canonical: location.origin + '/' });
  return landingView();
});
route('/login', false, (params, query) => authView(params, query));
route('/forgot', false, () => forgotView());
route('/reset', false, (params, query) => resetView(params, query));
route('/legal', false, (params, query) => termsView(params, query));
route('/compatibility', false, () => compatibilityView());

route('/browse', true, () => discoverView());
route('/search', true, (params, query) => searchView(params, query));
route('/live', true, () => liveView());
route('/iptv', true, (params, query) => iptvView(params, query));
route('/library', true, (params, query) => libraryView(params, query));
route('/series/:id', true, (params) => seriesDetailView(params));
route('/plans', true, (params, query) => plansView(params, query));
route('/settings', true, (params, query) => settingsView(params, query));
route('/checkout/test', true, (params, query) => mockPaymentView(params, query));
route('/checkout/details', true, (params, query) => checkoutDetailsView(params, query));
route('/checkout/done', true, () => checkoutDoneView());
route('/handoff', true, (params, query) => handoffView(params, query));
route('/admin', true, (params, query) => adminView(params, query));
route('/admin/users/:id', true, (params, query) => adminUserView(params, query));

// --- Eski bağlantılar: yer imleri ve paylaşılmış adresler kırılmasın ---------
const LEGACY_SECTION: Record<string, string> = {
  kullanim: 'terms', gizlilik: 'privacy', cerez: 'cookies', kvkk: 'kvkk',
  abonelik: 'subscription', guvenlik: 'safety', telif: 'copyright', iletisim: 'contact',
  kanallar: 'channels', filmler: 'movies', diziler: 'series', favoriler: 'favorites', devam: 'continue',
};
const legacy = (from: string, to: string | ((query: URLSearchParams) => string)) => {
  route(from, false, (_params, query) => {
    const target = typeof to === 'function' ? to(query) : to;
    navigate(target, true);
  });
};
const withSection = (path: string) => (query: URLSearchParams) => {
  const old = query.get('bolum');
  const section = old ? LEGACY_SECTION[old] ?? old : null;
  return section ? `${path}?section=${section}` : path;
};
legacy('/giris', (query) => {
  const mode = query.get('mod') === 'kayit' ? 'signup' : null;
  const next = query.get('devam');
  const params = new URLSearchParams();
  if (mode) params.set('mode', mode);
  if (next) params.set('next', next);
  const qs = params.toString();
  return '/login' + (qs ? `?${qs}` : '');
});
legacy('/kosullar', withSection('/legal'));
legacy('/uyumluluk', '/compatibility');
legacy('/izle', '/browse');
legacy('/kesfet', (query) => {
  const term = query.get('q');
  return term ? `/search?q=${encodeURIComponent(term)}` : '/browse';
});
legacy('/ara', (query) => {
  const term = query.get('q');
  return term ? `/search?q=${encodeURIComponent(term)}` : '/search';
});
legacy('/canli', '/live');
legacy('/listem', withSection('/library'));
legacy('/favoriler', '/library?section=favorites');
legacy('/filmler', '/iptv?section=movies');
legacy('/diziler', '/iptv?section=series');
legacy('/dizi/:id', '/browse');
legacy('/paketler', '/plans');
legacy('/ayarlar', '/settings');
legacy('/telefonda', '/settings');
legacy('/eslestir', '/login');

setNotFound(() => {
  // Bilinmeyen rota arama motorunda "yumuşak 404" olmasın.
  setPageMeta({ title: t('meta.notFound'), index: false });
  if (!state.session) { void landingView(); return; }
  ensureAppShell();
  mount(appMain(), el('div', { class: 'wrap' }, stateBlock({
    title: t('notFound.title'),
    body: t('notFound.body'),
    action: { label: t('common.goDiscover'), onClick: () => navigate('/browse') },
  })));
});

// Giriş gerektiren yola oturumsuz gelinirse giriş ekranına; oturumluyken giriş
// ekranına gelinirse uygulamaya. `/` her iki durumda da landing olarak açılır.
setGuard((path, auth) => {
  if (auth && !state.session) {
    // Sorgu da taşınır (ör. ödeme dönüşündeki `?payment=`, arama terimi).
    navigate(`/login?next=${encodeURIComponent(path + location.search)}`, true);
    return false;
  }
  if (!auth && state.session && path === '/login') { navigate('/browse', true); return false; }
  return true;
});

async function boot(): Promise<void> {
  const app = document.getElementById('app')!;
  // Açılış göstergesi sayfanın üst-ortasında: landing ilk boyanmadan önce
  // ekranın tam ortasında duran bir halka, sayfa yerleşimini yanıltıyordu.
  mount(app, el('div', { class: 'boot' }, el('div', { class: 'spinner' })));
  startSurfaceWatch();
  applyDocumentLang();
  await loadSession();
  startStats();
  startRouter();
  startVersionWatch(isPlaying);
  // Yüzey değişince (döndürme, pencere boyutu) ekran o bağlama göre yeniden
  // kurulur: araç düzeni masaüstü düzeninin küçültülmüşü değil, ayrı bir düzen.
  onSurfaceChange(() => { if (!isPlaying()) void resolve(); });
}

void boot();

// Sekme kapanırken açık oynatma oturumunu bırak: tek yayın kilidi kira bitene
// kadar beklemesin.
window.addEventListener('pagehide', () => {
  if (!isPlaying()) return;
  navigator.sendBeacon?.('/api/playback/beacon-close', '');
});
